import type { ReactElement } from 'react';
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Accordion from 'atom-components/accordion-lite';
import Card from 'atom-components/card';
import SystemErrorAlert from 'feature-components/system-error-alert';
import { pluralise } from 'helpers/string.helpers';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import type { TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import type { MarkResponseFormValues } from './mark-response-form.schema';
import Question from './question';
import type { UICategory } from './types';
import styles from './question-categories.module.scss';

type Props = {
  categories: UICategory[];
  canEditMark: boolean;
  canAdministrateMark: boolean;
  defaultValues: MarkResponseFormValues;
  markItemMarkers: TestInstanceResponseMarkerWithCustomerUser[];
  markItemMarkerOrganisationUsers: BaseCustomerUser[];
};

export default function QuestionCategories({
  categories,
  canEditMark,
  canAdministrateMark,
  defaultValues,
  markItemMarkers,
  markItemMarkerOrganisationUsers
}: Props): ReactElement {
  type CategoryVisibility = {
    categoryId: string;
    show: boolean;
  };
  const defaultVisibilities = categories.map((category) => ({
    categoryId: category.id,
    show: true
  }));
  const [categoryVisibilities, setCategoryVisibilities] =
    useState<CategoryVisibility[]>(defaultVisibilities);

  function toggleVisibility(categoryId: string): void {
    const newVisibilities = [...categoryVisibilities];
    const index = categoryVisibilities.findIndex((cv) => cv.categoryId === categoryId);
    newVisibilities[index].show = !categoryVisibilities[index].show;
    setCategoryVisibilities(newVisibilities);
  }

  function isVisible(categoryId: string): boolean {
    const categoryVisibility = categoryVisibilities.find((cv) => cv.categoryId === categoryId);
    return categoryVisibility ? categoryVisibility.show : false;
  }

  if (categories.length === 0) {
    return (
      <SystemErrorAlert>
        No rubric items were found for this response, so it can't be marked.
      </SystemErrorAlert>
    );
  }

  return (
    <>
      {categories.map((category) => (
        <Accordion key={category.id}>
          <Accordion.Header
            id={category.id}
            ariaExpanded={isVisible(category.id)}
            ariaControls={`category-collapse-${category.id}`}
            onClick={(): void => {
              toggleVisibility(category.id);
            }}
          >
            <Card.Heading level={3}>{category.name}</Card.Heading>
            <Accordion.CollapseControlButton
              id={`toggle-category-button-${category.id}`}
              ariaExpanded={isVisible(category.id)}
              ariaControls={`category-collapse-${category.id}`}
              onClick={(): void => toggleVisibility(category.id)}
            >
              <FontAwesomeIcon icon="pen-to-square" className="me-2" />
              {isVisible(category.id) ? 'Hide' : 'Show'}{' '}
              {pluralise('Question', category.questions.length)}
            </Accordion.CollapseControlButton>
          </Accordion.Header>
          <Accordion.Collapse
            id={category.id}
            ariaLabelledBy={`toggle-category-button-${category.id}`}
            isOpen={isVisible(category.id)}
          >
            <div className={styles.categoryBody}>
              {category.questions.map((question) => (
                <Question
                  key={question.id}
                  question={question}
                  showHeading={!category.isOrphanQuestionCategory} // Prevent heading duplication
                  canEditMark={canEditMark}
                  canAdministrateMark={canAdministrateMark}
                  defaultValues={defaultValues}
                  markItemMarkers={markItemMarkers}
                  markItemMarkerOrganisationUsers={markItemMarkerOrganisationUsers}
                />
              ))}
            </div>
          </Accordion.Collapse>
        </Accordion>
      ))}
    </>
  );
}
